import React from 'react';
import { Plus, Edit, Trash2, Search, Calendar, BarChart3 } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { eventoService, campoService } from '../services/api';

const Eventos = () => {
  const { data: eventos, loading, error, create, update, remove } = useCrud(eventoService, 'eventos');
  const { data: campos } = useCrud(campoService, 'campos');
  const modal = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');

  const validationRules = {
    nome: { required: true, label: 'Nome', minLength: 2, maxLength: 100 },
    campo_id: { required: true, label: 'Campo' },
    data_inicio: { required: true, label: 'Data de início' },
    data_fim: { required: true, label: 'Data de fim' }
  };

  const form = useForm(
    { nome: '', campo_id: '', data_inicio: '', data_fim: '', descricao: '' },
    validationRules
  );

  const filteredEventos = eventos.filter(evento =>
    evento.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    form.reset();
    modal.open({ mode: 'create', title: 'Novo Evento' });
  };

  const handleEdit = (evento) => {
    form.reset({
      nome: evento.nome || '',
      campo_id: evento.campo_id || '',
      data_inicio: evento.data_inicio ? evento.data_inicio.split('T')[0] : '',
      data_fim: evento.data_fim ? evento.data_fim.split('T')[0] : '',
      descricao: evento.descricao || ''
    });
    modal.open({ mode: 'edit', title: 'Editar Evento', id: evento.id });
  };

  const handleDelete = async (evento) => {
    if (window.confirm(`Deseja realmente excluir o evento "${evento.nome}"?`)) {
      await remove(evento.id);
    }
  };

  const handleSubmit = async (values) => {
    const result = modal.data.mode === 'create' 
      ? await create(values)
      : await update(modal.data.id, values);

    if (result.success) {
      modal.close();
      form.reset();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Eventos</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={20} />
          Novo Evento
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar eventos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
        </div>
      </div>

      <div className="card">
        {filteredEventos.length === 0 ? (
          <div className="empty-state">
            <Calendar className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhum evento encontrado</h3>
            <p className="mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando um novo evento.'}
            </p>
            {!searchTerm && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Novo Evento
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Campo</th>
                  <th>Data Início</th>
                  <th>Data Fim</th>
                  <th>Descrição</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredEventos.map((evento) => (
                  <tr key={evento.id}>
                    <td className="font-medium">{evento.nome}</td>
                    <td>{evento.Campo?.nome || 'N/A'}</td>
                    <td>{evento.data_inicio ? formatDate(evento.data_inicio) : '-'}</td>
                    <td>{evento.data_fim ? formatDate(evento.data_fim) : '-'}</td>
                    <td>{evento.descricao || '-'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => window.location.href = `/evento/${evento.id}/dashboard`}
                          className="btn btn-sm btn-primary"
                          title="Dashboard do Evento"
                        >
                          <BarChart3 size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(evento)}
                          className="btn btn-sm btn-secondary"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(evento)}
                          className="btn btn-sm btn-danger"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">×</button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  value={form.values.nome}
                  onChange={(e) => form.setValue('nome', e.target.value)}
                  onBlur={() => form.setTouched('nome')}
                  className={`form-input ${form.errors.nome && form.touched.nome ? 'border-red-500' : ''}`}
                  placeholder="Digite o nome do evento"
                />
                {form.errors.nome && form.touched.nome && (
                  <div className="form-error">{form.errors.nome}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Campo *</label>
                <select
                  value={form.values.campo_id}
                  onChange={(e) => form.setValue('campo_id', e.target.value)}
                  onBlur={() => form.setTouched('campo_id')}
                  className={`form-select ${form.errors.campo_id && form.touched.campo_id ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione um campo</option>
                  {campos.map((campo) => (
                    <option key={campo.id} value={campo.id}>
                      {campo.nome}
                    </option>
                  ))}
                </select>
                {form.errors.campo_id && form.touched.campo_id && (
                  <div className="form-error">{form.errors.campo_id}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Data de Início *</label>
                <input
                  type="date"
                  value={form.values.data_inicio}
                  onChange={(e) => form.setValue('data_inicio', e.target.value)}
                  onBlur={() => form.setTouched('data_inicio')}
                  className={`form-input ${form.errors.data_inicio && form.touched.data_inicio ? 'border-red-500' : ''}`}
                />
                {form.errors.data_inicio && form.touched.data_inicio && (
                  <div className="form-error">{form.errors.data_inicio}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Data de Fim *</label>
                <input
                  type="date"
                  value={form.values.data_fim}
                  onChange={(e) => form.setValue('data_fim', e.target.value)}
                  onBlur={() => form.setTouched('data_fim')}
                  className={`form-input ${form.errors.data_fim && form.touched.data_fim ? 'border-red-500' : ''}`}
                />
                {form.errors.data_fim && form.touched.data_fim && (
                  <div className="form-error">{form.errors.data_fim}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Descrição</label>
                <textarea
                  value={form.values.descricao}
                  onChange={(e) => form.setValue('descricao', e.target.value)}
                  className="form-input"
                  placeholder="Digite a descrição do evento"
                  rows="3"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Eventos;